import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  AssignmentDto,
  AssignmentReferencesDto,
  CreateAssignmentDto,
  MoveSessionEventDto,
  PlanningReferencesDto,
  SessionEventDto,
  SessionRescheduleReferencesDto,
  UpdateAssignmentDto,
} from '@shared/dto';
import { AbstractCrudApiService } from '@shared/abstracts/abstract-crud-api.service';
import { urlJoin } from '@shared/utils/url.utils';

export interface AssignmentMemberSessionsParams {
  memberId: number;
  assignmentId: number;
}

/** One seduta of one therapy of one member — the path of the per-seduta routes. */
export interface AssignmentMemberSessionEventParams extends AssignmentMemberSessionsParams {
  sessionEventId: number;
}

/**
 * Assignments under `/members/:memberId/assignments` — the member-scoped twin of
 * {@link AssignmentService} (`/assignments`). Named for the assignment domain it
 * lives in; the path still starts at `members`.
 */
@Injectable({ providedIn: 'root' })
export class AssignmentMemberService extends AbstractCrudApiService<
  AssignmentDto,
  CreateAssignmentDto,
  UpdateAssignmentDto
> {
  override get endpoint(): string {
    return `${this.api$.value}/:memberId/assignments/:assignmentId`;
  }

  constructor() {
    super();
    this.api$.next(urlJoin(this.env.get('api.base'), 'members'));
    this.logger.debug(this.name, 'Endpoint', this.endpoint);
  }

  readSessions(params: AssignmentMemberSessionsParams): Observable<SessionEventDto[]> {
    return this.http
      .get<SessionEventDto[]>(`${this.url({ memberId: params.memberId, assignmentId: params.assignmentId })}/session-events`)
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * What deleting this therapy would take away: its events bucketed into pending
   * (simply cancelled), performed (the member trained — this is what gets hidden)
   * and unattended, plus whether a session is running right now. A pure read.
   *
   * Drives the confirmation dialog that a 428
   * (`ASSIGNMENT.DELETE_REQUIRES_CONFIRMATION`) on `delete()` demands — the error
   * says only that a decision is needed, the counts behind it live here.
   */
  references(params: AssignmentMemberSessionsParams): Observable<AssignmentReferencesDto> {
    return this.http
      .get<AssignmentReferencesDto>(`${this.url({ memberId: params.memberId, assignmentId: params.assignmentId })}/references`)
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * The verdict the planning `update()` would reach, as data.
   *
   * The edit TRANSLATES the plan and never compresses it, so shortening a period
   * leaves sedute outside their own therapy — where the nightly seal writes them
   * off as missed. `outside` lists them at the position they WOULD have after the
   * edit, and `conflicts` covers the other half a client cannot deduce: a shift
   * can also collide with another therapy of the same member.
   *
   * Both dates are optional; omitting one keeps the stored value.
   */
  planningReferences(
    params: AssignmentMemberSessionsParams,
    range: { startAt?: string | null; endAt?: string | null },
  ): Observable<PlanningReferencesDto> {
    const query: Record<string, string> = {};
    if (range.startAt) query['startAt'] = range.startAt;
    if (range.endAt) query['endAt'] = range.endAt;
    return this.http
      .get<PlanningReferencesDto>(
        `${this.url({ memberId: params.memberId, assignmentId: params.assignmentId })}/planning-references`,
        { params: query },
      )
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Moves one pending seduta (SCHEDULED or DELAYED); anything else is history and
   * is refused with 409.
   *
   * Only `start` travels: the seduta keeps the duration it already has, so a
   * client cannot stretch it by sending an `end` of its own.
   *
   * The row is NOT rewritten in place — it stays as a `RESCHEDULED` record of what
   * had been planned and a new one carries the appointment. Both come back in the
   * payload; the caller still has to reload the events, since an `AssignmentDto`
   * does not carry them.
   */
  moveSessionEvent(params: AssignmentMemberSessionEventParams, start: string): Observable<AssignmentDto> {
    const body: MoveSessionEventDto = { start };
    return this.http
      .patch<AssignmentDto>(this.sessionEventUrl(params), body)
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Cancels one pending seduta. The row stays, marked `DELETED` — distinct from
   * the soft delete that removing the whole therapy applies to every seduta — so
   * it weighs nothing in progress or adherence and can still be restored.
   *
   * Refused with 409 on the last live seduta: emptying the plan one seduta at a
   * time would seal the therapy as NOT_COMPLETED without anything having happened.
   */
  cancelSessionEvent(params: AssignmentMemberSessionEventParams): Observable<AssignmentDto> {
    return this.http
      .delete<AssignmentDto>(this.sessionEventUrl(params))
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /** Puts a cancelled seduta back. The slot is re-checked, so a taken hour gives 409. */
  restoreSessionEvent(params: AssignmentMemberSessionEventParams): Observable<AssignmentDto> {
    return this.http
      .post<AssignmentDto>(`${this.sessionEventUrl(params)}/restore`, {})
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * The verdict {@link moveSessionEvent} would reach, as data — computed by the
   * same code as the write, so a preview cannot promise a move the save refuses.
   *
   * Says whether the seduta is movable at all, whether the instant is past or
   * outside the therapy window, and WHICH sedute it would overlap, including ones
   * belonging to another therapy of the same member.
   */
  rescheduleReferences(
    params: AssignmentMemberSessionEventParams,
    start: string,
  ): Observable<SessionRescheduleReferencesDto> {
    return this.http
      .get<SessionRescheduleReferencesDto>(`${this.sessionEventUrl(params)}/reschedule-references`, {
        params: { start },
      })
      .pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  private sessionEventUrl(params: AssignmentMemberSessionEventParams): string {
    const base = this.url({ memberId: params.memberId, assignmentId: params.assignmentId });
    return `${base}/session-events/${params.sessionEventId}`;
  }
}
